import React from "react";
import { Helmet } from "react-helmet-async";
import { Star } from "lucide-react";
import Header from "../components/Header";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";
import { businessReference } from "../seo/business";
import Footer from "../components/Footer";

const TestimonialsPage = () => {
  const allTestimonials = [
    {
      id: 1,
      name: "Sarah M.",
      location: "Thunder Bay, ON",
      text: "Groundhogs™ installed our new Eljen system last fall. Professional crew, clean worksite, and they handled all the permits. System works perfectly! Highly recommend for anyone in Northwestern Ontario needing septic work.",
      rating: 5,
      service: "Eljen Mound Installation"
    },
    {
      id: 2,
      name: "Robert K.",
      location: "Kenora, ON",
      text: "Had our old septic replaced with a new traditional system. The team was efficient, knowledgeable, and completed the job faster than expected. They explained everything clearly and left our property clean.",
      rating: 5,
      service: "Traditional Septic Replacement"
    },
    {
      id: 3,
      name: "Jennifer L.",
      location: "Dryden, ON",
      text: "Best septic company in Northwestern Ontario. They explained everything clearly and the installation was flawless. Worth every penny for the quality and professionalism.",
      rating: 5,
      service: "New Traditional Septic"
    },
    {
      id: 4,
      name: "Michael T.",
      location: "Thunder Bay, ON",
      text: "Our cottage property had challenging soil conditions. Groundhogs™ recommended an Eljen system and handled everything from permits to final inspection. Outstanding service and the system performs better than expected.",
      rating: 5,
      service: "Eljen Mound Installation"
    },
    {
      id: 5,
      name: "Lisa P.",
      location: "Fort Frances, ON",
      text: "We were worried about the disruption to our property, but the crew was incredibly respectful and professional. They restored our yard beautifully after the installation. Very impressed with Groundhogs™.",
      rating: 5,
      service: "Traditional Septic Replacement"
    },
    {
      id: 6,
      name: "David W.",
      location: "Thunder Bay, ON",
      text: "From the initial consultation to final inspection, everything was handled professionally. The estimate was accurate, timeline was met, and the crew was knowledgeable about Northwestern Ontario's unique challenges.",
      rating: 5,
      service: "New Eljen Mound System"
    },
    {
      id: 7,
      name: "Patricia R.",
      location: "Atikokan, ON",
      text: "Excellent work! Our property is in a remote area with difficult access, but Groundhogs™ handled it without any issues. They're true experts in septic installation.",
      rating: 5,
      service: "Traditional Septic Installation"
    },
    {
      id: 8,
      name: "James C.",
      location: "Thunder Bay, ON",
      text: "Had multiple quotes and Groundhogs™ wasn't the cheapest, but they were by far the most professional and thorough. A year later, we're so glad we went with quality. System works perfectly.",
      rating: 5,
      service: "Eljen Mound Replacement"
    },
    {
      id: 9,
      name: "Karen B.",
      location: "Kenora, ON",
      text: "The crew took time to explain the Eljen system and why it was best for our lakefront property. Installation was clean and efficient. Would definitely recommend to anyone.",
      rating: 5,
      service: "Eljen Mound Installation"
    }
  ];

  // Generate Review Schema for each testimonial
  const reviewSchemas = allTestimonials.map((testimonial) => ({
    "@type": "Review",
    "author": {
      "@type": "Person",
      "name": testimonial.name
    },
    "itemReviewed": businessReference,
    "reviewBody": testimonial.text,
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": testimonial.rating,
      "bestRating": 5,
      "worstRating": 1
    }
  }));

  // The verified aggregate lives in the single shared business record.
  const aggregateRatingSchema = {
    "@context": "https://schema.org",
    "@graph": reviewSchemas
  };

  return (
    <div>
      <SEO
        title="Septic Installation Testimonials | Groundhogs™ (Thunder Bay)"
        description="Read reviews from homeowners across Thunder Bay and Northwestern Ontario. 1,200+ installs and 15+ years of experience in engineered septic installation and replacement."
        canonicalPath="/testimonials"
      />
      <StructuredData id="testimonial-reviews-schema" type="Review" data={aggregateRatingSchema} />
      <Header />

      {/* Hero Section */}
      <section className="gh-intro pt-32 pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="gh-intro-heading text-center mb-12" data-reveal>
            <h1 className="text-5xl font-bold mb-4" style={{ color: "var(--dark-navy)" }}>
              Customer Reviews
            </h1>
            <p className="text-xl" style={{ color: "var(--primary-orange)" }}>
              See Why Northwestern Ontario Trusts Groundhogs™ Environmental
            </p>
            <div className="mt-6 flex justify-center items-center space-x-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={32} fill="var(--primary-orange)" style={{ color: "var(--primary-orange)" }} />
                ))}
              </div>
              <span className="text-2xl font-bold" style={{ color: "var(--dark-navy)" }}>4.9/5.0</span>
              <span className="text-lg" style={{ color: "var(--text-light)" }}>from 127+ reviews</span>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-16" style={{ backgroundColor: "var(--light-gray)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {allTestimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg" style={{ transition: "box-shadow 0.3s ease" }}>
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} fill="var(--primary-orange)" style={{ color: "var(--primary-orange)" }} />
                  ))}
                </div>
                <p className="mb-4 italic" style={{ color: "var(--text-dark)" }}>
                  &quot;{testimonial.text}&quot;
                </p>
                <div className="border-t pt-4">
                  <div className="font-bold" style={{ color: "var(--dark-navy)" }}>
                    {testimonial.name}
                  </div>
                  <div className="text-sm" style={{ color: "var(--text-light)" }}>
                    {testimonial.location}
                  </div>
                  <div className="text-sm mt-2 px-3 py-1 rounded inline-block" style={{ backgroundColor: "var(--light-gray)", color: "var(--primary-orange)" }}>
                    {testimonial.service}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-center" style={{ color: "var(--dark-navy)" }}>
            Why Our Customers Choose Us
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold mb-2" style={{ color: "var(--primary-orange)" }}>
                99%
              </div>
              <div className="text-lg" style={{ color: "var(--text-dark)" }}>Client Satisfaction Rate</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2" style={{ color: "var(--primary-orange)" }}>
                1200+
              </div>
              <div className="text-lg" style={{ color: "var(--text-dark)" }}>Installations Completed</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2" style={{ color: "var(--primary-orange)" }}>
                15+
              </div>
              <div className="text-lg" style={{ color: "var(--text-dark)" }}>Years of Experience</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2" style={{ color: "var(--primary-orange)" }}>
                5 Year
              </div>
              <div className="text-lg" style={{ color: "var(--text-dark)" }}>Installation Warranty</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16" style={{ backgroundColor: "var(--light-gray)" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4" style={{ color: "var(--dark-navy)" }}>
            Join Our Satisfied Customers
          </h2>
          <p className="text-xl mb-8" style={{ color: "var(--text-light)" }}>
            Experience the Groundhogs™ difference with your septic installation
          </p>
          <a
            href="tel:1-888-907-3777"
            className="inline-block px-8 py-4 rounded-md font-bold text-white text-lg hover:opacity-90"
            style={{ backgroundColor: "var(--primary-orange)" }}
          >
            Get Your Free Estimate
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TestimonialsPage;